import { GetScopeSearchRequest, GetScopeValuesRequest, ScopeItem, TopologyService } from "../../Scopes.types";
import { centralAdminDagSchema, centralAdminMachineSchema } from "Shared/Components/Scope/Topology/CentralAdminTopology";
import { getSelectedEnvironment, useSubstrateEnvironmentRingTopologyService } from "./useSubstrateEnvironmentRingTopologyService";

import { CentralAdminEntityType } from "Enums/CentralAdminEntityType";
import { Constants } from "Shared/Constants";
import { useScopeSearch } from "../useScopeSearch";
import { useSubstrateForest } from "./useSubstrateForest";
import { useSubstrateRegion } from "./useSubstrateRegion";
import { useORPCClient } from "Core/oRPC";

export const useSubstrateLogicalTopologyService = (): TopologyService => {
    const { inventoryApi } = useORPCClient();
    const envRingTopologyService = useSubstrateEnvironmentRingTopologyService();
    const { getRegionList } = useSubstrateRegion();
    const { getForestList } = useSubstrateForest();
    const { getScopeSearchResult } = useScopeSearch();

    return {
        getScopeValueOptions: async (request: GetScopeValuesRequest): Promise<ScopeItem[]> => {
            const currentScopeTarget = request.currentScopeTarget;
            const workload = request.context?.Workload ?? Constants.defaultWorkload;

            const environment = getSelectedEnvironment(request.selectedScopesSoFar || []);
            switch (currentScopeTarget.ScopeType.toLowerCase()) {
                case "region":
                    return getRegionList(environment, workload);
                case "forest":
                    return getForestList(environment, workload, request.selectedScopesSoFar, false);
                case "dag":
                    const selectedForest = request.selectedScopesSoFar?.find((scope) => scope.ScopeType?.toLowerCase() === "forest");
                    const dagRequest = {
                        Environment: environment,
                        Workload: workload || "",
                        TargetType: CentralAdminEntityType.Dag,
                        FilterType: CentralAdminEntityType.Forest,
                        FilterValue: selectedForest?.Value?.toUpperCase()
                    };
                    const dagOut = await inventoryApi.postTopology(dagRequest);
                    const dagParsedOutput = centralAdminDagSchema.array().parse(dagOut);
                    return dagParsedOutput.map((item) => ({
                        ScopeType: "Dag",
                        Value: item.Name,
                        ExtraProperties: { Forest: item.Forest || "", Region: item.Region || "", ForestAlias: item.ForestAlias || "" }
                    }));
                case "machine":
                    const selectedDag = request.selectedScopesSoFar?.find(
                        (scope) => scope.ScopeType?.toLowerCase() === "dag" || scope.ScopeType?.toLowerCase() === "exodag"
                    );
                    const machineRequest = {
                        Environment: environment,
                        Workload: workload || "",
                        TargetType: CentralAdminEntityType.Machine,
                        FilterType: CentralAdminEntityType.Dag,
                        FilterValue: selectedDag?.Value?.toUpperCase()
                    };
                    const machineOut = await inventoryApi.postTopology(machineRequest);
                    const machineParsedOutput = centralAdminMachineSchema.array().parse(machineOut);
                    return machineParsedOutput.map((item) => ({
                        ScopeType: "Machine",
                        Value: item.Name,
                        ExtraProperties: { Forest: item.Forest || "", Region: item.Region || "", ForestAlias: item.ForestAlias || "", Dag: item.Dag || "" }
                    }));
                default:
                    return envRingTopologyService.getScopeValueOptions(request);
            }
        },
        getScopeSearch: (request: GetScopeSearchRequest): Promise<ScopeItem[]> => {
            const validTargetTypes = [CentralAdminEntityType.Region, CentralAdminEntityType.Forest, CentralAdminEntityType.Dag, CentralAdminEntityType.Machine];
            return getScopeSearchResult(request, validTargetTypes);
        }
    };
};
